import { CustomFilterWithGrid } from 'components/CustomTableFilter';
import { useRequest } from 'features/request/useRequest';
import { useResetMultipleStoreData } from 'features/request/useResetStoreData';
import type { CatalogOtherCostVariableItemData, ProjectMethodologyEnum } from 'gql/graphql';
import type { FilterComponentProps, TableFilters } from 'uikit';

import type React from 'react';
import { useEffect, useMemo } from 'react';

import {
    otherCostVariablesGroupFilterGql,
    otherCostVariablesNameFilterGql,
    otherCostVariablesSubsidiaryNameFilterGql,
} from '../catalogQueries';

export const useCommonVariablesFilters = ({
    catalogId,
    globalOnly,
    methodology,
    showSubsidiaryFilter = false,
}: {
    catalogId: string | undefined;
    globalOnly: boolean;
    methodology: ProjectMethodologyEnum;
    showSubsidiaryFilter?: boolean;
}) => {
    const memoizedFilterIds = useMemo(
        () => [
            'otherCostVariablesNameFilter',
            'otherCostVariablesGroupFilter',
            'otherCostVariablesSubsidiaryNameFilter',
        ],
        [],
    );

    const [resetVariablesFilters] = useResetMultipleStoreData({
        requestIds: memoizedFilterIds,
    });

    useEffect(
        () => () => {
            resetVariablesFilters();
        },
        [resetVariablesFilters],
    );

    const [otherCostVariablesNameFilterData, , , otherCostVariablesNameFilterRequest] = useRequest({
        requestId: 'otherCostVariablesNameFilter',
        query: otherCostVariablesNameFilterGql,
        withEffect: false,
    });

    const [otherCostVariablesGroupFilterData, , , otherCostVariablesGroupFilterRequest] = useRequest({
        requestId: 'otherCostVariablesGroupFilter',
        query: otherCostVariablesGroupFilterGql,
        withEffect: false,
    });

    const [otherCostVariablesSubsidiaryNameFilterData, , , otherCostVariablesSubsidiaryNameFilterRequest] = useRequest({
        requestId: 'otherCostVariablesSubsidiaryNameFilter',
        query: otherCostVariablesSubsidiaryNameFilterGql,
        withEffect: false,
    });

    const filterVariables = useMemo(
        () => ({
            catalogUniqueId: catalogId,
            globalOnly,
            methodology,
        }),
        [catalogId, globalOnly, methodology],
    );

    return useMemo<TableFilters<CatalogOtherCostVariableItemData>>(() => {
        const filters: TableFilters<CatalogOtherCostVariableItemData> = [
            {
                id: 'name',
                name: 'Наименование переменной: ',
                field: 'name',
                filterer: () => true,
                component: {
                    name: CustomFilterWithGrid as React.FC<FilterComponentProps>,
                    props: {
                        withSearch: true,
                        withSelectAllBtn: true,
                        items: otherCostVariablesNameFilterData?.catalogOtherCostVariablesNameFilter?.items,
                        requestFn: otherCostVariablesNameFilterRequest,
                        requestVariables: filterVariables,
                        requestQuery: otherCostVariablesNameFilterGql,
                        dataAccessor: 'catalogOtherCostVariablesNameFilter',
                        childFieldAccessor: 'value',
                        columnDefs: [
                            {
                                headerName: 'Наименование переменной',
                                field: 'value',
                                lockVisible: true,
                                flex: 1,
                                checkboxSelection: true,
                            },
                        ],
                        whereVariables: ['value'],
                    },
                },
            },
            {
                id: 'groupName',
                name: 'Группа переменной: ',
                field: 'groupName',
                filterer: () => true,
                component: {
                    name: CustomFilterWithGrid as React.FC<FilterComponentProps>,
                    props: {
                        withSearch: false,
                        withSelectAllBtn: true,
                        items: otherCostVariablesGroupFilterData?.catalogOtherCostVariablesGroupFilter?.items,
                        requestFn: otherCostVariablesGroupFilterRequest,
                        requestVariables: filterVariables,
                        requestQuery: otherCostVariablesGroupFilterGql,
                        dataAccessor: 'catalogOtherCostVariablesGroupFilter',
                        childFieldAccessor: 'value',
                        columnDefs: [
                            {
                                headerName: 'Группа переменной',
                                field: 'value',
                                lockVisible: true,
                                flex: 1,
                                checkboxSelection: true,
                            },
                        ],
                        whereVariables: ['value'],
                    },
                },
            },
        ];

        if (showSubsidiaryFilter) {
            filters.push({
                id: 'subsidiaryName',
                name: 'Дочернее общество: ',
                field: 'subsidiaryName',
                filterer: () => true,
                component: {
                    name: CustomFilterWithGrid as React.FC<FilterComponentProps>,
                    props: {
                        withSearch: true,
                        withSelectAllBtn: true,
                        items: otherCostVariablesSubsidiaryNameFilterData?.catalogOtherCostVariablesSubsidiaryNameFilter
                            ?.items,
                        requestFn: otherCostVariablesSubsidiaryNameFilterRequest,
                        requestVariables: filterVariables,
                        requestQuery: otherCostVariablesSubsidiaryNameFilterGql,
                        dataAccessor: 'catalogOtherCostVariablesSubsidiaryNameFilter',
                        childFieldAccessor: 'value',
                        columnDefs: [
                            {
                                headerName: 'Дочернее общество',
                                field: 'value',
                                lockVisible: true,
                                flex: 1,
                                checkboxSelection: true,
                            },
                        ],
                        whereVariables: ['value'],
                    },
                },
            });
        }

        return filters;
    }, [
        filterVariables,
        otherCostVariablesGroupFilterData?.catalogOtherCostVariablesGroupFilter?.items,
        otherCostVariablesGroupFilterRequest,
        otherCostVariablesNameFilterData?.catalogOtherCostVariablesNameFilter?.items,
        otherCostVariablesNameFilterRequest,
        otherCostVariablesSubsidiaryNameFilterData?.catalogOtherCostVariablesSubsidiaryNameFilter?.items,
        otherCostVariablesSubsidiaryNameFilterRequest,
        showSubsidiaryFilter,
    ]);
};
